import React, { useEffect, useState } from 'react';
// import { useFirestoreConnect } from "react-redux-firebase";
import { Layout, Skeleton } from 'antd';
import SideNav from '../../../Layout/SideNav';
// import { getGsmPlantData, getSensor } from "../../../modules/sensors/queries";
import HeaderBar from '../../../Layout/HeaderBar';
import { useParams } from 'react-router';
import LevelChart from 'Views/LevelSensorOverview/Graph';
import DrumVisualization from './DrumVisualization';
import IBCVisualization from './IBCVisualization';
import WellVisualization from './WellVisualization';
import firebase from '../../../firebase';
import {
  useLatestLevelPercentage,
  type TContainerType,
} from 'Views/LevelSensorOverview/hooks/useLatestSensorLog';
import { SensorType } from '@/general/enums';
import { useTranslation } from 'react-i18next';
import LevelConfig from './LevelConfig';
import EditSensorSettings from '../components/EditSensorSettings';
import SensorInternalOverview from '../components/SensorInternalOverview';

interface LevelSensor {
  deviceId: string;
  title: string;
  containerType: TContainerType;
  thresholds?: {
    binderMin?: number;
  };
  internalResponsibles?: string[];
  orgId: string;
  createTime: Date;
  type: SensorType.LEVEL_SENSOR_INTERNAL;
}

export default function LevelSensorInternalContainer() {
  const { orgId, sensorId } = useParams<{ orgId: string; sensorId: string }>();
  const [currentSensor, setCurrentSensor] = useState<LevelSensor | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { t } = useTranslation();

  const {
    levelPercent,
    thresholdPercent,
    levelLiters,
    thresholdLiters,
    maxCapacityLiters,
    isLoading: sensorLoading,
  } = useLatestLevelPercentage(
    currentSensor?.deviceId!,
    currentSensor?.containerType!,
    currentSensor?.thresholds?.binderMin
  );

  useEffect(() => {
    if (!orgId || !sensorId) {
      return;
    }
    let unsubscribe: any = null;
    unsubscribe = firebase
      .firestore()
      .collection(`orgs/${orgId}/sensors`)
      .doc(sensorId)
      .onSnapshot(
        (query) => {
          if (query.exists) {
            setCurrentSensor(query.data() as LevelSensor);
          }
          setIsLoading(false);
        },
        (error) => {
          console.error(error);
          setIsLoading(false);
        }
      );
    return unsubscribe;
  }, [orgId, sensorId]);

  return (
    <>
      <SideNav />
      <Layout>
        <HeaderBar />
        <div className="p-8 space-y-6">
          <h3 className="text-2xl font-bold">{currentSensor?.title}</h3>
          <SensorInternalOverview
            orgId={orgId}
            sensorId={sensorId}
            sensorType={SensorType.LEVEL_SENSOR_INTERNAL}
          />
          {!isLoading && currentSensor ? (
            <div className="space-y-8">
              {/* Container Visualizations */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {/* Drum Visualization */}
                {currentSensor?.containerType === 'DRUM_220' && (
                  <div className="flex flex-col items-center">
                    <h4 className="text-lg font-semibold mb-4 text-gray-700">
                      {t('sensor.container.type.drum_220.title')}
                    </h4>
                    <DrumVisualization
                      levelPercent={levelPercent}
                      thresholdPercent={thresholdPercent}
                      currentLiters={levelLiters}
                      thresholdLiters={thresholdLiters}
                      maxCapacityLiters={maxCapacityLiters}
                    />
                  </div>
                )}

                {/* IBC Visualization */}
                {currentSensor?.containerType === 'IBC_1000' && (
                  <div className="flex flex-col items-center">
                    <h4 className="text-lg font-semibold mb-4 text-gray-700">
                      {t('sensor.container.type.ibc_1000.title')}
                    </h4>
                    <IBCVisualization
                      levelPercent={levelPercent}
                      thresholdPercent={thresholdPercent}
                      currentLiters={levelLiters}
                      thresholdLiters={thresholdLiters}
                      maxCapacityLiters={maxCapacityLiters}
                    />
                  </div>
                )}

                {/* Well Visualization */}
                {currentSensor?.containerType === 'WELL_10' && (
                  <div className="flex flex-col items-center">
                    <h4 className="text-lg font-semibold mb-4 text-gray-700">
                      {t('sensor.container.type.well_10.title')}
                    </h4>
                    <WellVisualization
                      levelPercent={levelPercent}
                      thresholdPercent={thresholdPercent}
                      currentLiters={levelLiters}
                      thresholdLiters={thresholdLiters}
                      maxCapacityLiters={maxCapacityLiters}
                      wellDepth={10}
                    />
                  </div>
                )}
              </div>

              <LevelConfig deviceId={currentSensor?.deviceId} />

              <EditSensorSettings
                orgId={orgId!}
                sensorId={sensorId!}
                sensorType={currentSensor.type}
                currentInternalResponsibles={currentSensor.internalResponsibles}
                currentContainerType={currentSensor.containerType}
              />

              {/* Level Chart */}
              <div className="w-full">
                <h4 className="text-lg font-semibold mb-4 text-gray-700">
                  {t('sensor.container.type.level_history.title')}
                </h4>
                <LevelChart
                  id={currentSensor?.deviceId}
                  containerType={currentSensor?.containerType}
                  thresholds={currentSensor.thresholds}
                />
              </div>
            </div>
          ) : (
            <Skeleton />
          )}
        </div>
      </Layout>
    </>
  );
}
